import React, { useCallback, useEffect, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt, faSpinner, faWindowClose } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

import Modal from "../components/modal";
import FilterInputField from "../components/filter-input-field";
import PaginationControls from "../components/pagination-controls";
import Checkbox from "../components/checkbox";
import { fetch } from "../utils/api-wrapper";
import { cachedFetch } from "../utils/cached-api-wrapper";
import Utils from "../utils/utils";
import CCardsConfigurationNavigationBar from "../components/c-card-configuration-navigation-bar";
import AsyncSelect from "react-select/async";

const CreditCardCategories = ({ data }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [filterValue, setFilterValue] = useState("");
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [updatedCategory, setUpdatedCategory] = useState({});
    const [categoriesDeleteSelection, setCategoriesDeleteSelection] = useState([]);
    const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const [suppliersFilterValue, setSuppliersFilterValue] = useState("");

    // const canManageCategories = Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]);
    const canManageCategories = true;

    const sorting = useMemo(() => [{ id: "name", desc: false }], []);

    const normalizeSupplier = useCallback((supplier) => {
        if (!supplier) return null;
        if (supplier.supplier_id && supplier.names) {
            return {
                supplier_id: supplier.supplier_id,
                names: supplier.names
            };
        }
        const supplierId = supplier._id?.$oid ?? supplier._id ?? supplier.id ?? null;
        const names = supplier.names ?? (supplier.name ? [supplier.name] : []);
        if (!supplierId) return null;
        return { supplier_id: supplierId, names };
    }, []);

    const refreshCategories = useCallback(() => {
        setLoading(true);
        let start = (page - 1) * pageSize;
        const filters = [];
        if (filterValue?.trim()) {
            filters.push({ id: "search", value: filterValue.trim() });
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}` +
            `&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}` +
            `&start=${encodeURIComponent(start)}` +
            `&page_size=${pageSize}`;
        fetch(`/c_card_category/${searchData}`, "GET", {},
            (response) => {
                setCategories(response.data?.categories ?? []);
                setTotalRowCount(response.data?.meta?.totalRowCount ?? response.data?.categories?.length ?? 0);
                setLoading(false);
            },
            () => {
                // setErrorMessage(data.t.read("cc_categories.error_fetching"));
                setLoading(false);
            });
    }, [data.t, filterValue, page, pageSize, sorting]);

    const loadSuppliers = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        // const searchData = `${encodeURIComponent(val)}?matter_id=${matter_id}`;
        cachedFetch(`/suppliers/search/${val}`, "GET", {}, (response) => {
            const data = response.data;
            // callback(data.map(activityMap));
        });
    };
    const refreshSuppliers = useCallback(() => {
        const suppliersStart = 0;
        const suppliersPageSize = 10;
        const suppliersSorting = [{ id: "names", desc: false }];
        const suppliersFilters = [];
        if (suppliersFilterValue?.trim()) {
            suppliersFilters.push({ id: "search", value: suppliersFilterValue.trim() });
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(suppliersFilters ?? []))}` +
            `&sorting=${encodeURIComponent(JSON.stringify(suppliersSorting ?? []))}` +
            `&start=${encodeURIComponent(suppliersStart)}` +
            `&page_size=${suppliersPageSize}`;
        fetch(`/suppliers/${searchData}`, "GET", {},
            (response) => {
                setSuppliers(response.data?.suppliers ?? []);
            });
    }, [suppliersFilterValue]);

    useEffect(() => {
        refreshCategories();
    }, [refreshCategories]);

    useEffect(() => {
        loadSuppliers()
        refreshSuppliers();
    }, [refreshSuppliers]);

    useEffect(() => {
        setCategoriesDeleteSelection([]);
    }, [page, pageSize, filterValue]);

    const openNewCategoryModal = () => {
        if (!canManageCategories) return;
        setUpdatedCategory({
            name: "",
            supplier: null,
            id_client: data.id_client
        });
        setShowCategoryModal(true);
    };

    const openUpdateCategoryModal = (category) => {
        if (!canManageCategories) return;
        setUpdatedCategory({
            ...category,
            supplier: normalizeSupplier(category?.supplier),
            id_client: category?.id_client ?? data.id_client
        });
        setShowCategoryModal(true);
    };

    const cancelCategoryModal = () => {
        setShowCategoryModal(false);
        setUpdatedCategory({});
    };

    const handleCategorySubmit = (e) => {
        e.preventDefault();
        if (!updatedCategory?.name?.trim() || !updatedCategory?.supplier?.supplier_id) {
            setErrorMessage(data.t.read("errors.requirements_not_fulfilled"));
            return;
        }
        setShowCategoryModal(false);
        if (updatedCategory?._id) {
            updateCategory();
        } else {
            createCategory();
        }
    };

    const createCategory = () => {
        const payload = {
            name: updatedCategory.name.trim(),
            supplier: normalizeSupplier(updatedCategory.supplier),
            id_client: updatedCategory.id_client ?? data.id_client
        };
        let formData = new FormData();
        formData.append("category", JSON.stringify(payload));
        fetch("/c_card_category/", "POST", formData,
            () => {
                setSuccessMessage(data.t.read("cc_categories.created"));
                setErrorMessage(null);
                setUpdatedCategory({});
                refreshCategories();
            },
            () => {
                setErrorMessage(data.t.read("error"));
            });
    };

    const updateCategory = () => {
        const categoryId = updatedCategory._id?.$oid ?? updatedCategory._id;
        const payload = {
            name: updatedCategory.name.trim(),
            supplier: normalizeSupplier(updatedCategory.supplier),
            id_client: updatedCategory.id_client ?? data.id_client
        };
        let formData = new FormData();
        formData.append("category", JSON.stringify(payload));
        fetch(`/c_card_category/${categoryId}`, "PUT", formData,
            () => {
                setSuccessMessage(data.t.read("cc_categories.updated"));
                setErrorMessage(null);
                setUpdatedCategory({});
                refreshCategories();
            },
            () => {
                setErrorMessage(data.t.read("failed_to_update"));
            });
    };

    const deleteCategories = () => {
        fetch("/c_card_category/", "DELETE", { "category_ids": categoriesDeleteSelection },
            () => {
                setSuccessMessage(data.t.read("cc_categories.deleted"));
                setErrorMessage(null);
                setCategoriesDeleteSelection([]);
                setShowDeleteCategoryModal(false);
                refreshCategories();
            },
            () => {
                setErrorMessage(data.t.read("failed_to_delete"));
                setShowDeleteCategoryModal(false);
            });
    };

    const cancelDeleteCategoryModal = () => {
        setShowDeleteCategoryModal(false);
        setCategoriesDeleteSelection([]);
    };

    const isAllCategoriesSelected = () => {
        if (!categories?.length) {
            return false;
        }
        return categories.every(category => categoriesDeleteSelection.includes(category?._id?.$oid));
    };

    const toggleAllCategoriesSelected = () => {
        if (!canManageCategories) return;
        if (isAllCategoriesSelected()) {
            setCategoriesDeleteSelection([]);
        } else {
            setCategoriesDeleteSelection(categories.map(category => category?._id?.$oid).filter(Boolean));
        }
    };

    const toggleCategorySelected = (category) => {
        if (!canManageCategories) return;
        const id = category?._id?.$oid;
        if (!id) return;
        if (categoriesDeleteSelection.includes(id)) {
            setCategoriesDeleteSelection(categoriesDeleteSelection.filter(catId => catId !== id));
        } else {
            setCategoriesDeleteSelection([...categoriesDeleteSelection, id]);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPageSize(rows);
        if (page !== 1) {
            setPage(1);
        }
    };

    const handleFilterValueChange = (value) => {
        if (page !== 1) {
            setPage(1);
        }
        setFilterValue(value);
    };

    const supplierOptions = suppliers.map(supplier => ({
        label: supplier?.names?.[0] ?? supplier?.name ?? data.t.read("n_a"),
        value: supplier
    }));

    const supplierValue = updatedCategory?.supplier
        ? {
            value: updatedCategory.supplier,
            label: updatedCategory.supplier?.names?.[0] ?? data.t.read("n_a")
        }
        : null;

    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = categories.length ? indexOfFirstItem + categories.length - 1 : indexOfFirstItem - 1;
    const totalPages = Math.max(1, Math.ceil((totalRowCount || 0) / pageSize));
    const emptyRowsCount = Math.max(0, pageSize - categories.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    return (
        <div className="card pl-4 pr-4 pb-3 rounded-lg">
            <CCardsConfigurationNavigationBar data={data} page="categories" />
            <div>
                <div className='pagination-row'>
                    <FilterInputField
                        filterValue={filterValue}
                        setFilterValue={handleFilterValueChange}
                        placeholder={data.t.read("search")}
                    />
                    <PaginationControls
                        page={page}
                        totalPages={totalPages}
                        indexOfFirstItem={indexOfFirstItem}
                        indexOfLastItem={indexOfLastItem}
                        handlePageChange={handlePageChange}
                        totalItems={totalRowCount}
                        data={data}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPage={pageSize}
                    />
                </div>
                <div className="d-flex justify-content-between align-items-center py-2">
                    <div />
                    <div className="d-flex">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm ml-2"
                            disabled={!canManageCategories || categoriesDeleteSelection.length === 0}
                            onClick={() => setShowDeleteCategoryModal(true)}
                        >
                            {`${data.t.read("delete")} (${categoriesDeleteSelection.length})`}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm ml-2"
                            disabled={!canManageCategories}
                            onClick={openNewCategoryModal}
                        >
                            {data.t.read("add")}
                        </button>
                    </div>
                </div>
                <div className="mb-2 card rounded-lg admin-table">
                    {loading ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        </div>
                    ) : (
                        <table className='table table-hover font-small'>
                            <thead>
                            <tr>
                                <th>
                                    <Checkbox
                                        disabled={!canManageCategories || !categories?.length}
                                        isChecked={isAllCategoriesSelected()}
                                        onChange={toggleAllCategoriesSelected}
                                    />
                                </th>
                                <th className={"col-6"}>{data.t.read("name")}</th>
                                <th className={"col-6"}>{data.t.read("supplierName")}</th>
                                <th>{data.t.read("client_table.actions")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {categories.map((category) => (
                                <tr key={category?._id?.$oid}>
                                    <td>
                                        <Checkbox
                                            disabled={!canManageCategories}
                                            isChecked={categoriesDeleteSelection.includes(category?._id?.$oid)}
                                            onChange={() => toggleCategorySelected(category)}
                                        />
                                    </td>
                                    <td onClick={canManageCategories ? () => openUpdateCategoryModal(category) : undefined}>
                                        {category?.name ?? data.t.read("n_a")}
                                    </td>
                                    <td onClick={canManageCategories ? () => openUpdateCategoryModal(category) : undefined}>
                                        {category?.supplier?.names?.[0] ?? data.t.read("n_a")}
                                    </td>
                                    <td>
                                        <FontAwesomeIcon
                                            className="mr-2"
                                            icon={faPencilAlt}
                                            type="button"
                                            onClick={() => {
                                                if (!canManageCategories) return;
                                                openUpdateCategoryModal(category);
                                            }}
                                            style={{ cursor: canManageCategories ? "pointer" : "not-allowed" }}
                                        />
                                        <FontAwesomeIcon
                                            icon={faWindowClose}
                                            type="button"
                                            style={{ color: 'red', cursor: canManageCategories ? "pointer" : "not-allowed" }}
                                            onClick={() => {
                                                if (!canManageCategories) return;
                                                setShowDeleteCategoryModal(true);
                                                setCategoriesDeleteSelection([category?._id?.$oid]);
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                            {emptyRows.map((_, idx) => (
                                <tr key={`empty-${idx}`}>
                                    <td colSpan="4">&nbsp;</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCategoryModal && (
                <form onSubmit={handleCategorySubmit}>
                    <Modal
                        title={updatedCategory?._id ? data.t.read("edit") : data.t.read("add")}
                        okIsSubmit={true}
                        cancelAction={cancelCategoryModal}
                        okText={data.t.read("save")}
                        cancelText={data.t.read("cancel")}
                    >
                        <div className="form-group">
                            <label className="font-weight-bold">{data.t.read("name")}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={updatedCategory?.name ?? ""}
                                onChange={(e) => setUpdatedCategory({ ...updatedCategory, name: e.target.value })}
                                required
                                disabled={!canManageCategories}
                            />
                        </div>
                        <div className="form-group">
                            <label className="font-weight-bold">{data.t.read("supplierName")}</label>
                            {/*<AsyncSelect*/}
                            {/*    key={`supplier-select-${updatedCategory?._id}`}*/}
                            {/*    styles={Utils.styleSelect}*/}
                            {/*    menuPortalTarget={document.body}*/}
                            {/*    value={null}*/}
                            {/*    onChange={(choice) => {*/}
                            {/*       */}
                            {/*    }}*/}
                            {/*    classNamePrefix="select"*/}
                            {/*    isDisabled={!canManageCategories}*/}
                            {/*    name="activity-select"*/}
                            {/*    placeholder={data.t.read("invoice.activity")}*/}
                            {/*    isClearable*/}
                            {/*    defaultOptions*/}
                            {/*    cacheOptions*/}
                            {/*    loadOptions={loadActivities}*/}
                            {/*/>*/}
                            <Select
                                name="supplier"
                                value={supplierValue}
                                options={supplierOptions}
                                styles={Utils.styleSelect}
                                classNamePrefix="select"
                                placeholder={data.t.read("supplierName")}
                                onInputChange={(value, { action }) => {
                                    if (action === "input-change") {
                                        setSuppliersFilterValue(value);
                                    }
                                }}
                                onChange={(selected) => {
                                    if (!selected) {
                                        setUpdatedCategory({ ...updatedCategory, supplier: null });
                                        return;
                                    }
                                    setUpdatedCategory({
                                        ...updatedCategory,
                                        supplier: normalizeSupplier({
                                            supplier_id: selected.value?._id?.$oid ?? selected.value?._id ?? selected.value?.supplier_id,
                                            names: selected.value?.names ?? (selected.label ? [selected.label] : [])
                                        })
                                    });
                                }}
                                isClearable
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                isDisabled={!canManageCategories}
                            />
                            <input
                                type="text"
                                className="d-none"
                                required
                                value={updatedCategory?.supplier?.supplier_id ?? ""}
                                readOnly
                            />
                        </div>
                    </Modal>
                </form>
            )}

            {showDeleteCategoryModal && (
                <Modal
                    title={data.t.read("delete")}
                    okAction={deleteCategories}
                    cancelAction={cancelDeleteCategoryModal}
                    okText={data.t.read("delete")}
                    cancelText={data.t.read("cancel")}
                >
                    {data.t.read("cc_categories.delete_confirmation")}
                </Modal>
            )}
            
            {errorMessage && (
                <Modal
                    title={data.t.read("error")}
                    okAction={() => setErrorMessage(null)}
                    okText={"OK"}>
                    <p style={{ whiteSpace: 'pre-line' }} className='text-danger'>{errorMessage}</p>
                </Modal>
            )}

            {successMessage && (
                <Modal
                    title={data.t.read("success")}
                    okAction={() => setSuccessMessage(null)}
                    okText={"OK"}>
                    {successMessage}
                </Modal>
            )}
        </div>
    );
};

export default inject("data")(observer(CreditCardCategories));
